import { canvasToBlob } from 'molstar/lib/mol-canvas3d/util';
import { SsaoParams } from 'molstar/lib/mol-canvas3d/passes/ssao';
import { PluginBehaviors } from 'molstar/lib/mol-plugin/behavior';
import { PluginConfig } from 'molstar/lib/mol-plugin/config';
import { PluginContext } from 'molstar/lib/mol-plugin/context';
import { PluginSpec } from 'molstar/lib/mol-plugin/spec';
import { Task } from 'molstar/lib/mol-task';
import { Color } from 'molstar/lib/mol-util/color';
import { ParamDefinition as PD } from 'molstar/lib/mol-util/param-definition';

const DEFAULT_SPIN_SPEED = 0.05;

const DefaultSsaoParams = PD.getDefaultValues(SsaoParams);

function ignoreZeroSizeResize(plugin, viewport) {
    const handleResize = plugin.handleResize;
    plugin.handleResize = () => {
        if (!viewport || !viewport.offsetWidth || !viewport.offsetHeight) return;
        handleResize();
    };
}

export async function createStructurePlugin(canvas, viewport, options = {}) {
    const plugin = new PluginContext({
        actions: [],
        behaviors: [
            PluginSpec.Behavior(PluginBehaviors.Representation.HighlightLoci),
            PluginSpec.Behavior(PluginBehaviors.Representation.SelectLoci),
            PluginSpec.Behavior(PluginBehaviors.Camera.FocusLoci),
            PluginSpec.Behavior(PluginBehaviors.Camera.CameraControls),
            PluginSpec.Behavior(PluginBehaviors.CustomProps.SecondaryStructure),
            PluginSpec.Behavior(PluginBehaviors.CustomProps.ValenceModel),
        ],
        animations: [],
        config: [
            [PluginConfig.General.Transparency, 'blended'],
        ],
    });
    await plugin.init();
    ignoreZeroSizeResize(plugin, viewport);

    const ok = await plugin.initViewerAsync(canvas, viewport);
    if (ok === false) {
        throw new Error('Mol* viewer initialization failed');
    }

    applyViewerCanvasProps(plugin, options);
    return plugin;
}

export function parseColor(value, fallback = 0xffffff) {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return Color(value);
    }
    if (typeof value === 'string') {
        const hex = value.trim().replace(/^#/, '');
        const parsed = Number.parseInt(hex, 16);
        if (Number.isFinite(parsed)) {
            return Color(parsed);
        }
    }
    return Color(fallback);
}

export function applyViewerCanvasProps(plugin, options = {}) {
    const canvas3d = plugin?.canvas3d;
    if (!canvas3d) return;

    canvas3d.setProps({
        transparentBackground: true,
        sceneRadiusFactor: 3.0,
        cameraFog: { name: 'off', params: {} },
        renderer: {
            ...canvas3d.props.renderer,
            ...(options.renderer || {}),
        },
        marking: options.marking
            ? {
                ...canvas3d.props.marking,
                ...options.marking,
            }
            : canvas3d.props.marking,
        camera: {
            helper: { axes: { name: 'off', params: {} } },
            fov: 60,
        },
        cameraClipping: {
            radius: 100,
            far: false,
            minNear: 0.1,
        },
        postprocessing: {
            enabled: true,
            occlusion: options.occlusion || { name: 'on', params: DefaultSsaoParams },
            shadow: { name: 'off', params: {} },
            outline: options.outline || {
                name: 'on',
                params: {
                    scale: 0.5,
                    threshold: 0.1,
                    color: Color(0x111111),
                    includeTransparent: true,
                },
            },
            dof: { name: 'off', params: {} },
            bloom: {
                name: 'on',
                params: {
                    strength: 1,
                    radius: 0,
                    threshold: 0,
                    mode: 'emissive',
                },
            },
            antialiasing: {
                name: 'smaa',
                params: {
                    edgeThreshold: 0.1,
                    maxSearchSteps: 16,
                },
            },
        },
    });
}

export function setCanvasSpin(plugin, enabled) {
    if (!plugin?.canvas3d) return;
    const trackball = plugin.canvas3d.props.trackball;
    plugin.canvas3d.setProps({
        trackball: {
            ...trackball,
            animate: enabled
                ? { name: 'spin', params: { speed: DEFAULT_SPIN_SPEED, axis: [0, 1, 0] } }
                : { name: 'off', params: {} },
        },
    });
}

export async function drawStableFrame(plugin) {
    plugin?.canvas3d?.commit(true);
    await new Promise(resolve => requestAnimationFrame(resolve));
    plugin?.canvas3d?.commit(true);
    await new Promise(resolve => requestAnimationFrame(resolve));
}

export async function captureViewportPng(plugin, configure, taskName = 'Generate Image') {
    const screenshot = plugin?.helpers?.viewportScreenshot;
    if (!screenshot) return null;

    await drawStableFrame(plugin);

    const previousValues = screenshot.values;
    const previousCropParams = screenshot.cropParams;
    configure(screenshot, previousValues, previousCropParams);

    try {
        return await plugin.runTask(Task.create(taskName, async (ctx) => {
            await screenshot.draw(ctx);
            return canvasToBlob(screenshot.canvas, 'image/png');
        }));
    } finally {
        screenshot.behaviors.values.next(previousValues);
        screenshot.behaviors.cropParams.next(previousCropParams);
        screenshot.resetCrop();
    }
}
